import React, { createContext, useContext, useMemo } from 'react';
import { useRouter } from 'next/compat/router';
import { consumerDispensaries } from 'shared/graphql/dispensary/queries';
import { useQuery } from 'hooks/use-query';
import { getVariables } from 'src/dispensary/hooks/use-dispensary';
import { useCollectionChannel } from 'src/hooks/use-collection-channel';
import type { Dispensaries } from 'src/gql/graphql';

type ConsumerDispensariesData = {
  filteredDispensaries?: Dispensaries[] | null;
};

export function computeSkip(
  routerIsReady: boolean | undefined,
  isClient: boolean,
  resolvedId: string | undefined
): boolean {
  return (!routerIsReady && isClient) || !resolvedId;
}

const StorefrontBootstrapContext = createContext<Dispensaries | null>(null);

type StorefrontBootstrapProviderProps = {
  children: React.ReactNode;
  /** Explicit override; falls back to the `cName`/`cname` route param when omitted. */
  cName?: string;
};

export function StorefrontBootstrapProvider({ children, cName }: StorefrontBootstrapProviderProps): JSX.Element {
  const router = useRouter();
  const routerIsReady = router?.isReady;
  const { cName: routeCName, cname: routeCname } = (router?.query ?? {}) as { cName?: string; cname?: string };
  const resolvedId = cName ?? routeCName ?? routeCname;

  const collectionChannel = useCollectionChannel();

  const { data } = useQuery<ConsumerDispensariesData, ReturnType<typeof getVariables>>(consumerDispensaries, {
    variables: getVariables(resolvedId, undefined, collectionChannel),
    skip: computeSkip(routerIsReady, typeof window !== 'undefined', resolvedId),
  });

  const dispensary = useMemo<Dispensaries | null>(() => {
    const results = data?.filteredDispensaries ?? [];
    return results.find((candidate) => candidate.cName === resolvedId || candidate.id === resolvedId) ?? null;
  }, [data, resolvedId]);

  return <StorefrontBootstrapContext.Provider value={dispensary}>{children}</StorefrontBootstrapContext.Provider>;
}

/** Returns the fat dispensary document from the nearest `StorefrontBootstrapProvider`, or `null` when unmounted. */
export function useStorefrontBootstrap(): Dispensaries | null {
  return useContext(StorefrontBootstrapContext);
}
